import { rem } from 'polished'
import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Divider from 'LuxKit/Divider'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import AccommodationFacilities, { getPropertyFacilitiesContent } from 'components/Common/AccommodationDetails/AccommodationFacilities'
import AccommodationThingsToKnow from 'components/Common/AccommodationDetails/AccommodationThingsToKnow'
import { usesPropertyThingsToKnowLayout } from 'components/Common/AccommodationDetails/PropertyThingsToKnowContent'
import LayoutContainer from 'components/Common/LayoutContainer'
import StaticPropertyMapWithInteractiveModal from 'components/Common/PropertyMap/StaticPropertyMapWithInteractiveModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import ModalContext from 'contexts/ModalContext'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { hasMapCoordinates } from './mapCoordinates'
import OfferLocationMap from './OfferLocationMap'
import OpenInGoogleMapsButton, { LocationAddressRow } from './OpenInGoogleMapsButton'
import useNavigableOfferContentScroll, {
  NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
} from './useNavigableOfferContentScroll'
import { useNavigableOfferContentFocusTargets } from './useNavigableOfferContentFocusTargets'

import NavigationBar from './NavigableOfferContentBar'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import { StructuredFacilitiesReworkStandaloneActivation } from './StructuredFacilitiesReworkActivation'
import TraderInformation from './TraderInformation'
import UserReviewSection from './UserReviewSection'
import { maybeTrackReadMoreClientEvent } from './analytics'
import {
  buildStructuredFacilitiesActivationPlacement,
  usePropertyStructuredFacilitiesRework,
  type StructuredFacilitiesActivationPlacement,
} from './useStructuredFacilitiesRework'
import { OfferPageSnapshotContributionRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import { buildOfferPageAccordionContribution } from 'tripPlanner/assistant/surfaces/offerPage/offerPageAccordionContribution'

const PROPERTY_LOCATION_MAP_HEIGHT = 258
const TwoColumns = styled.div`
  ${mediaQueryUp.tablet} {
    columns: 2;

    > * {
      break-inside: avoid;
    }
  }
`

const Category = styled.li`
  display: flex;
  align-items: center;
  margin: 0 0 ${rem(16)};

  & > svg {
    min-width: ${rem(20)};
    margin-inline-end: ${rem(12)};
  }
`

function propertyHasCoordinates(property: App.Property.Property): boolean {
  return hasMapCoordinates(property.location)
}

type ReviewOptions = {
  property: App.OfferProperty,
  isLPP: false
} | {
  property: App.BedbankProperty,
  isLPP: true
}

function getUserReviewProps(
  { rating }: App.Property.Property,
  { lpcOfferIds, lpcPropertyIds, bedbankPropertyIds }: App.PropertyOfferMapping,
): ReviewOptions | undefined {
  if (!(rating?.reviewsTotal && rating.score)) return
  if (lpcOfferIds.length && lpcPropertyIds.length) {
    return {
      // Here, we're shimming just enough of `App.OfferProperty` for `<UserReviewSection/>` to work.
      // TODO(HP-2081): Make review components support `App.Property.Property`.
      property: {
        id: lpcPropertyIds[0],
        rating,
      } as App.OfferProperty,
      isLPP: false,
    }
  }
  if (bedbankPropertyIds.length) {
    return {
      // Here, we're shimming just enough of `App.BedbankProperty` for `<UserReviewSection/>` to work.
      // TODO(HP-2081): Make review components support `App.Property.Property`.
      property: {
        id: bedbankPropertyIds[0],
        rating,
      } as App.BedbankProperty,
      isLPP: true,
    }
  }
}

interface PropertyLocationMapProps {
  property: App.Property.Property
}

function PropertyLocationMap({ property }: PropertyLocationMapProps) {
  const { id, name, location } = property
  const markers = useMemo<Array<App.MapMarker>>(() => [{
    id,
    type: PROPERTY_MAP_MARKER_TYPE,
    latitude: location.latitude,
    longitude: location.longitude,
    name,
    address: location.formattedAddress,
  }], [id, location.formattedAddress, location.latitude, location.longitude, name])

  return (
    <StaticPropertyMapWithInteractiveModal
      height={PROPERTY_LOCATION_MAP_HEIGHT}
      markers={markers}
      modalTitle={name}
    />
  )
}

interface Props {
  hash?: string
  offer?: App.Offer | App.HotelOffer | App.BedbankOffer
  property: App.Property.Property
  propertyOfferMapping: App.PropertyOfferMapping
  traderInformation?: App.TraderInformation
}

interface ContentField {
  id: string
  label: string
  open: boolean
  collapsable: boolean
  initialHeight?: number
  minimumContentHeight?: number
  unconstrainedWhenExpanded?: boolean
  contentComponent: React.ReactElement
}

interface ContentFieldsOptions {
  intl: IntlShape
  structuredFacilitiesRework?: Array<App.StructuredFacilityGroup>
  shouldTrackFacilitiesExperimentInView: boolean
}

function getContentFields(props: Props, options: ContentFieldsOptions): {
  contentFields: Array<ContentField>
  structuredFacilitiesActivationPlacement?: StructuredFacilitiesActivationPlacement
} {
  const { hash, property, propertyOfferMapping } = props
  const {
    intl,
    structuredFacilitiesRework,
    shouldTrackFacilitiesExperimentInView,
  } = options

  const contentFields: Array<ContentField> = []

  if (propertyHasCoordinates(property)) {
    const { name, location, locationDescription } = property
    const offerLocationMapOffer = props.offer?.property &&
      hasMapCoordinates(props.offer.property) &&
      (isLEHotel(props.offer) || isBedbankOffer(props.offer)) ?
      props.offer :
      undefined

    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'ansXdPq2raOVESfnmWSz',
        defaultMessage: 'Location',
        description: 'Tab label and heading for location section on property page',
      }),
      open: hash === '#getting-there',
      collapsable: true,
      initialHeight: 410,
      contentComponent: <VerticalSpacer gap={16}>
        {offerLocationMapOffer && (
          <OfferLocationMap
            offer={offerLocationMapOffer}
            height={PROPERTY_LOCATION_MAP_HEIGHT}
          />
        )}
        {!offerLocationMapOffer && <PropertyLocationMap property={property} />}
        <div>
          <BodyText variant="medium">{name}</BodyText>
          <LocationAddressRow>
            <BodyText variant="medium" weight="bold">{location.formattedAddress}</BodyText>
            <OpenInGoogleMapsButton
              query={location.formattedAddress}
              placeId={location.placeId}
            />
          </LocationAddressRow>
        </div>
        {locationDescription && <MarkdownRender fontSize="medium" type="compact" content={locationDescription} />}
      </VerticalSpacer>,
    })
  }

  const facilitiesContent = getPropertyFacilitiesContent(property, structuredFacilitiesRework)
  const facilitiesLabel = intl.formatMessage({
    id: 'aZ2H92jVjph-myrruY4o',
    defaultMessage: 'Facilities',
    description: 'Tab label and heading for facilities section on property page',
  })

  // Capture the shared facilities-slot index before any arm-specific section is pushed so
  // control (often standalone-only) and variant (accordion) activate at the same Y position.
  const facilitiesSectionInsertIndex = contentFields.length

  if (facilitiesContent) {
    contentFields.push({
      id: 'facilities',
      label: facilitiesLabel,
      open: hash === '#facilities',
      collapsable: true,
      initialHeight: facilitiesContent.kind === 'structured' ? 240 : 120,
      unconstrainedWhenExpanded: facilitiesContent.kind === 'structured',
      contentComponent: <AccommodationFacilities content={facilitiesContent} />,
    })
  }

  const { structuredFinePrint, taxesAndFeesContent } = property
  const shouldUseBackendFinePrint = usesPropertyThingsToKnowLayout(property)
  if (!shouldUseBackendFinePrint && structuredFinePrint?.safety?.length) {
    contentFields.push({
      id: 'fine-print-safety',
      label: intl.formatMessage({
        id: '_qo-tV0npGQE3tZx1WXp',
        defaultMessage: 'Cleaning and safety measures',
        description: 'Tab label and heading for cleaning and safety measures section on property page',
      }),
      open: hash === '#fine-print-safety',
      collapsable: false,
      contentComponent: <TwoColumns>
        {structuredFinePrint.safety.map(safetyAttribute => (
          <Category key={safetyAttribute}>
            <LineCheckIcon size="S" />{safetyAttribute}
          </Category>
        ))}
      </TwoColumns>,
    })
  }

  if (shouldUseBackendFinePrint) {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: '9WsTECHeqsvJ8UhB4MQQ',
        defaultMessage: 'Things to know',
        description: 'Form field label for things to know section on the offer detail page',
      }),
      open: hash === '#fine-print',
      collapsable: false,
      contentComponent: <AccommodationThingsToKnow source={{ kind: 'property', property }} />,
    })
  } else {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: 'YqdvgkZpA8r_JTJljKDk',
        defaultMessage: 'Fine print',
        description: 'Tab label and heading for fine print section on property page',
      }),
      open: !!taxesAndFeesContent || hash === '#fine-print',
      collapsable: true,
      contentComponent: <AccommodationThingsToKnow source={{ kind: 'property', property }} />,
    })
  }

  const userReviewProps = getUserReviewProps(property, propertyOfferMapping)
  if (userReviewProps) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'lptUZobxlUcD8f_mqLvq',
        defaultMessage: 'Reviews',
        description: 'Tab label and heading for reviews section on property page',
      }),
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: <UserReviewSection {...userReviewProps} />,
    })
  }

  return {
    contentFields,
    structuredFacilitiesActivationPlacement: buildStructuredFacilitiesActivationPlacement({
      shouldTrackFacilitiesExperimentInView,
      facilitiesSectionInsertIndex,
    }),
  }
}

function PropertyNavigableOfferContent(props: Props) {
  const intl = useIntl()
  const { hash, offer, property, propertyOfferMapping } = props
  const {
    structuredFacilitiesReworkActivationRef,
    structuredFacilitiesRework,
    shouldTrackFacilitiesExperimentInView,
  } = usePropertyStructuredFacilitiesRework(property)
  const {
    contentFields,
    structuredFacilitiesActivationPlacement,
  } = useMemo(
    () => getContentFields(props, {
      intl,
      structuredFacilitiesRework,
      shouldTrackFacilitiesExperimentInView,
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- destructured props fields used as stable deps
    [
      hash,
      offer,
      property,
      propertyOfferMapping,
      intl,
      structuredFacilitiesRework,
      shouldTrackFacilitiesExperimentInView,
    ],
  )
  const defaultOpenField = contentFields.find(field => field.open)
  const [openFields, setOpenFields] = useState<Record<string, boolean>>(defaultOpenField ? { [defaultOpenField.id]: true } : {})
  const [headerSelected, setHeaderSelected] = useState<string | undefined>(defaultOpenField ? defaultOpenField?.id : undefined)
  const { contentGroupRefs, scrollToContentGroup } = useNavigableOfferContentScroll(
    NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
  )
  const accordionContribution = useMemo(
    () => buildOfferPageAccordionContribution('navigable', headerSelected),
    [headerSelected],
  )

  const onGroupInView = useCallback((id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
    }
  }, [])

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToContentGroup(id)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields, scrollToContentGroup])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    maybeTrackReadMoreClientEvent(id, !openFields[id])
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  // Idempotent expand (no scroll) used as the assistant focus-target prepare step.
  const openSection = useCallback((id: string) => {
    setOpenFields(previous => (previous[id] ? previous : { ...previous, [id]: true }))
  }, [])

  useNavigableOfferContentFocusTargets({
    contentFields,
    contentGroupRefs,
    openSection,
    scrollOffset: NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
  })

  const showModal = useContext(ModalContext)

  const openTraderInformationModal = useCallback(() => {
    if (!props.traderInformation) return
    showModal(<Modal
      title={intl.formatMessage({
        id: 'MuDJoj6jSqUQP2DLjtAb',
        defaultMessage: 'About this supplier',
        description: 'Title for the supplier information modal on property offer pages',
      })}
    >
      <TraderInformation traderInformation={props.traderInformation} />
    </Modal>)
  }, [showModal, props.traderInformation, intl])

  return (
    <>
      <OfferPageSnapshotContributionRegistration contribution={accordionContribution} />
      <NavigationBar
        contentFields={contentFields}
        onClick={onHeaderClick}
        selectedHeader={headerSelected}
      />
      <LayoutContainer variant="large">
        <Sheet padding="20 0" tabletPadding="56 0">
          <VerticalSpacer gap={20}>
            {contentFields.map((contentField, index) => <React.Fragment key={contentField.id}>
              {structuredFacilitiesActivationPlacement?.insertIndex === index && (
                <StructuredFacilitiesReworkStandaloneActivation
                  activationRef={structuredFacilitiesReworkActivationRef}
                />
              )}
              {index !== 0 && <Divider kind="secondary" direction="horizontal" />}
              <NavigableOfferContentGroup
                heading={contentField.label}
                id={contentField.id}
                ref={(node) => { contentGroupRefs.current[contentField.id] = node }}
                onInViewChange={onGroupInView}
                initialHeight={contentField.initialHeight ?? 96}
                minimumContentHeight={contentField.minimumContentHeight ?? 0}
                unconstrainedWhenExpanded={contentField.unconstrainedWhenExpanded}
                open={!!openFields[contentField.id]}
                onToggle={onReadMoreToggle}
                collapsable={contentField.collapsable}
              >
                <BodyText variant="medium">
                  {contentField.contentComponent}
                </BodyText>
              </NavigableOfferContentGroup>
            </React.Fragment>)}
            {structuredFacilitiesActivationPlacement?.insertIndex === contentFields.length && (
              <StructuredFacilitiesReworkStandaloneActivation
                activationRef={structuredFacilitiesReworkActivationRef}
              />
            )}
            {props.traderInformation && (
              <div id="trader-information">
                <TextLink size="medium" onClick={openTraderInformationModal}>
                  {intl.formatMessage({
                    id: 'Zt91_RAcVadDv7RoFaqO',
                    defaultMessage: 'About this supplier',
                    description: 'Link label to open the supplier information modal on property pages',
                  })}
                </TextLink>
              </div>
            )}
          </VerticalSpacer>
        </Sheet>
      </LayoutContainer>
    </>
  )
}

export default PropertyNavigableOfferContent
